/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useToaster } from "contexts/toasterContext";
import {
    CalendarDays,
    Users,
    CreditCard,
    Loader2,
    ArrowLeft,
    PackageCheck,
    Calculator
} from "lucide-react";

// Adjust the relative path to where your AppConfig file is located
import appConfig from "../../src/services/apiConfig";

export interface InvoiceType {
    id: number;
    registrationId: number;
    companyName: string;
    planTypeName: string;
    billingMonth: string;
    openingCount: number;
    addedCount: number;
    removedCount: number;
    billableCount: number;
    chargePerEmployee: number;
    totalAmount: number;
    status: string;
    dueDate: string;
    paidAt: string | null;
    razorpayOrderId: string | null;
    createdAt: string;
}

const PendingInvoices = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { addToast } = useToaster();

    // Support reading schemaName from query params if returning from another page, fallback to state/session
    const searchParams = new URLSearchParams(location.search);
    const querySchemaName = searchParams.get("schemaName");

    const resolvedSchemaName =
        querySchemaName ||
        location.state?.schemaName ||
        sessionStorage.getItem("schemaName") ||
        "";

    const [pendingInvoices, setPendingInvoices] = useState<InvoiceType[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        if (!resolvedSchemaName) {
            addToast("Session expired or invalid schema. Please try again.", "error");
            navigate(-1);
            return;
        }

        const fetchInvoices = async () => {
            try {
                setIsLoading(true);
                const regApiUrl = appConfig.getRegistrationApiUrl();
                const cleanBaseUrl = regApiUrl.endsWith('/') ? regApiUrl : `${regApiUrl}/`;

                const response = await axios.get(`${cleanBaseUrl}company/pending-invoices?schemaName=${resolvedSchemaName}`);

                const invoices = response.data?.data || response.data || [];
                const pending = Array.isArray(invoices) ? invoices.filter((inv: any) => inv.status === 'PENDING') : [];

                setPendingInvoices(pending);

                // If no pending invoices are found, redirect to plan selection
                if (pending.length === 0) {
                    navigate('/plan-selection', { state: location.state });
                }
            } catch (error) {
                console.error("Failed to fetch pending invoices:", error);
                addToast("Failed to load invoices. Please try again later.", "error");
            } finally {
                setIsLoading(false);
            }
        };

        fetchInvoices();
    }, [resolvedSchemaName, navigate, addToast, location.state]);

    const handlePayInvoice = (invoice: InvoiceType) => {
        const regApiUrl = appConfig.getRegistrationApiUrl();
        const urlObj = new URL(regApiUrl);
        const backendOrigin = urlObj.origin;

        let paymentUrl = `${backendOrigin}/payment.html?invoiceId=${invoice.id}`;

        // Fetch the base product URL dynamically using apiConfig
        let frontendBaseUrl = appConfig.getFrontendUrl();

        // Clean up trailing slash to form {product-url} and prevent double slashes (404 error)
        if (frontendBaseUrl.endsWith('/')) {
            frontendBaseUrl = frontendBaseUrl.slice(0, -1);
        }

        const callbackUrl = encodeURIComponent(`${frontendBaseUrl}/plan-selection?schemaName=${resolvedSchemaName}`);
        paymentUrl += `&callbackUrl=${callbackUrl}`;

        // Navigate to the payment URL on the registration backend
        window.location.href = paymentUrl;
    };

    if (isLoading) {
        return (
            <div className="min-h-[calc(100vh-70px)] flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-900">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                    <p className="text-gray-600 dark:text-gray-400 font-medium">Loading pending invoices...</p>
                </div>
            </div>
        );
    }

    if (pendingInvoices.length === 0) {
        return null; // Will trigger redirect in useEffect
    }

    return (
        <div className="min-h-[calc(100vh-70px)] flex items-center justify-center p-4 pb-16 bg-gray-50 dark:bg-gray-900 font-sans">
            {/* Root Container strictly locks maximum height */}
            <div className="w-full max-w-4xl bg-white dark:bg-gray-800 rounded-[20px] shadow-xl border border-gray-100 dark:border-gray-700 flex flex-col max-h-[85vh] md:max-h-[80vh] overflow-hidden">

                {/* Header Section (Fixed, won't scroll) */}
                <div className="bg-[#3b82f6] px-7 py-6 shrink-0">
                    <div className="flex items-center gap-3">
                        <div>
                            <h1 className="text-[22px] font-bold text-white leading-tight">Pending Invoices</h1>
                            <p className="text-blue-100 text-[13px] mt-0.5">Please clear your pending dues to continue managing your plan.</p>
                        </div>
                    </div>
                </div>

                {/* Main Content Area (Scrollable internally) */}
                <div className="p-6 md:p-8 flex flex-col flex-1 gap-6 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700">
                    {pendingInvoices.map((inv) => (
                        <div
                            key={inv.id}
                            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden flex flex-col shrink-0"
                        >
                            {/* Invoice Item Header */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center px-7 py-5 border-b border-gray-100 dark:border-gray-700 gap-4 shrink-0">
                                <div className="flex items-center gap-3.5">
                                    <div className="p-2.5 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
                                        <PackageCheck className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900 dark:text-white text-[17px] leading-tight">
                                            {inv.planTypeName} Plan
                                        </h3>
                                        <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5 font-medium">
                                            {inv.companyName}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-medium bg-[#fef2f2] text-[#ef4444] dark:bg-red-900/20 dark:text-red-400 border border-[#fee2e2] dark:border-red-800/30">
                                        <CalendarDays className="w-4 h-4" />
                                        Due: {new Date(inv.dueDate).toLocaleDateString()}
                                    </span>
                                </div>
                            </div>

                            {/* Invoice Details Grid */}
                            <div className="p-7 flex flex-col gap-7 shrink-0">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                    <div className="flex flex-col space-y-1.5">
                                        <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Product Name</p>
                                        <p className="font-medium text-gray-900 dark:text-white text-[15px]">Liriworks</p>
                                    </div>
                                    <div className="flex flex-col space-y-1.5">
                                        <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Billing Duration</p>
                                        <p className="font-medium text-gray-900 dark:text-white text-[15px]">
                                            {new Date(inv.billingMonth).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                                        </p>
                                    </div>
                                    <div className="flex flex-col space-y-1.5">
                                        <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Employees</p>
                                        <div className="flex items-center gap-1.5 font-medium text-gray-900 dark:text-white text-[15px]">
                                            <Users className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                                            {inv.billableCount}
                                        </div>
                                    </div>
                                    <div className="flex flex-col space-y-1.5">
                                        <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Price per Employee</p>
                                        <p className="font-medium text-gray-900 dark:text-white text-[15px]">INR {inv.chargePerEmployee}</p>
                                    </div>
                                </div>

                                {/* Pricing Calculation & Action Block (shrink-0 ensures it is never squished/hidden) */}
                                <div className="shrink-0 bg-[#f8fafc] dark:bg-gray-800/60 rounded-xl p-5 border border-[#f1f5f9] dark:border-gray-700 flex flex-col md:flex-row justify-between items-center gap-6">
                                    <div className="flex flex-col w-full md:w-auto">
                                        <div className="flex items-center gap-1.5 mb-2.5 text-[13px] text-gray-500 dark:text-gray-400 font-medium">
                                            <Calculator className="w-[15px] h-[15px]" />
                                            <span>Pricing Calculation</span>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-3 font-medium text-gray-600 dark:text-gray-300">
                                            <div className="bg-white dark:bg-gray-700 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm text-gray-900 dark:text-gray-100 flex items-center text-sm">
                                                INR {inv.chargePerEmployee}
                                            </div>
                                            <span className="text-gray-400 dark:text-gray-500 text-[13px]">×</span>
                                            <div className="bg-white dark:bg-gray-700 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm text-gray-900 dark:text-gray-100 flex items-center text-sm">
                                                {inv.billableCount} Employee{inv.billableCount !== 1 ? 's' : ''}
                                            </div>
                                            <span className="text-gray-400 dark:text-gray-500 text-xl leading-none mx-1">=</span>
                                            <span className="text-[22px] font-bold text-[#2563eb] dark:text-blue-400 tracking-tight">
                                                INR {inv.totalAmount}
                                            </span>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => handlePayInvoice(inv)}
                                        className="w-full md:w-auto px-8 py-[11px] bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-xl font-semibold transition-colors shadow-sm flex items-center justify-center gap-2 shrink-0"
                                    >
                                        <CreditCard className="w-5 h-5" />
                                        Pay Now
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* BOTTOM SECTION (Fixed footer) */}
                <div className="px-8 py-5 bg-[#fafafa] dark:bg-gray-800/80 border-t border-gray-100 dark:border-gray-700 flex justify-start shrink-0">
                    <button
                        type="button"
                        onClick={() => navigate('/admin/profile')}
                        className="px-5 py-2 flex items-center justify-center gap-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium transition-colors text-sm bg-white dark:bg-gray-800 shadow-sm"
                    >
                        <ArrowLeft size={16} />
                        Back
                    </button>
                </div>

            </div>
        </div>
    );
};

export default PendingInvoices;